import React from 'react';
import { Box, Button, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { emptyVariant } from './emptyProduct';

/**
 * Product variants list — each variant is a sellable SKU.
 *
 * Attributes are entered as raw JSON for now. A friendlier
 * Size/Color editor lives behind a feature flag in the wizard
 * codebase; until that ships, the JSON field stays so brand owners
 * with non-trivial option sets can still set them by hand.
 */
export default function ProductVariantsEditor({ form, setForm }) {
  const setVar = (i, patch) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
  const addVar = () =>
    setForm((f) => ({ ...f, variants: [...f.variants, emptyVariant()] }));
  const rmVar = (i) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='6px'>
        <Typography fontWeight='700' variant='body2'>Variants</Typography>
        <Button size='small' startIcon={<AddIcon />} onClick={addVar}>Add variant</Button>
      </Stack>
      <Typography variant='caption' color='text.secondary' mb='8px' display='block'>
        Each variant is a sellable SKU. Use attributes like Size / Color to distinguish them.
      </Typography>
      {form.variants.map((v, i) => (
        <Box
          key={v._id || `new-${i}`}
          sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '10px', mb: '8px' }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px' alignItems='center'>
            <TextField
              size='small' label='SKU'
              value={v.sku || ''}
              onChange={(e) => setVar(i, { sku: e.target.value.toUpperCase() })}
              placeholder='AUTO if blank'
              sx={{ flex: 1 }}
            />
            <TextField
              size='small' label='Attributes (JSON)'
              value={JSON.stringify(v.attributes || {})}
              onChange={(e) => {
                // Only accept a parse-success — silently ignore intermediate
                // invalid JSON so the user can edit mid-string without
                // losing focus / state.
                try { setVar(i, { attributes: JSON.parse(e.target.value || '{}') }); }
                catch (_) { /* ignore until valid */ }
              }}
              placeholder='{"Size":"M","Color":"Black"}'
              sx={{ flex: 2 }}
            />
            <TextField
              size='small' type='number' label='Price'
              value={v.price ?? ''}
              onChange={(e) => setVar(i, { price: e.target.value === '' ? null : Number(e.target.value) })}
              placeholder='inherit'
              sx={{ maxWidth: 110 }}
            />
            <Switch
              size='small' checked={v.is_active !== false}
              onChange={(e) => setVar(i, { is_active: e.target.checked })}
            />
            <IconButton size='small' color='error' onClick={() => rmVar(i)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
